<template>
  <VContainer class="check-your-entitlement-page">
    <VRow v-if="isLoading">
      <div class="d-flex flex-column align-center justify-center py-4 w-100">
        <VProgressCircular indeterminate color="primary" />
      </div>
    </VRow>
    <VRow v-else>
      <VCol cols="12">
        <PDFPrintComponent
          ref="pdfPrintRef"
          filename="uk-pension-entitlement"
          :hide-buttons="false"
          print-title="UK Pension Entitlement Report"
        >
          <div class="content">
            <div class="content__text">
              Hi <span class="text-main-color">{{ application.firstName + ' ' + application.lastName }}</span>
            </div>
            <div class="content__text">
              You have <span class="text-main-color">{{ application.uk_years }}</span> qualifying year(s) of NICs paid
              originally in the UK.
            </div>
            <div class="content__text">
              Your entitlement is approx.
              <span class="text-main-color">{{ formatCurrency(application.entitlement) }}</span> per year from your
              retirement age (<span class="text-main-color">{{ application.pension_age }}</span
              >).
            </div>
            <div class="hr"></div>
            <div class="content__subtext">Tick the years you have paid voluntary NI Contributions for:</div>
            <div class="content__note">Do not include the years you had originally.</div>
            <div class="tax-year-allocation">
              <div class="tax-year-allocation__actions">
                <div class="title">Tax Year Allocation</div>
                <div class="btns">
                  <button class="white-btn" @click="handleTickAll()">Tick all</button>
                  <button class="white-btn" @click="handleClearAll()">Clear</button>
                </div>
              </div>
              <table class="table">
                <thead>
                  <tr>
                    <th><div>Tax Year</div></th>
                    <th><div class="d-flex flex-column align-center justify-center">Purchased</div></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in application.taxYearAllocation" :key="item.label">
                    <td>{{ item.label }}</td>
                    <td>
                      <div class="d-flex flex-column align-center justify-center">
                        <input
                          type="checkbox"
                          :checked="item.isChecked"
                          @click="item.isChecked = !item.isChecked"
                          :disabled="isLoadingSaveTaxYearAllocation"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="tax-year-allocation__actions">
                <div class="notes">
                  Tip: It doesn’t matter how much / little you paid for any specific year. Tick it if you paid for it.
                </div>
                <div>
                  <button
                    class="main-btn"
                    @click="handleSaveTaxYearAllocation"
                    :disabled="isLoadingSaveTaxYearAllocation"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
            <div class="content__info">
              Since you bought <span class="text-main-color">{{ application.numberOfTickBoxes }}</span> year(s) your
              entitlement is approx. <br />
              <span class="text-main-color">{{ formatCurrency(application.fullEntitlement) }}</span> per year from your
              retirement age (<span class="text-main-color">{{ application.pension_age }}</span
              >).
            </div>
            <div class="d-flex flex-column align-center justify-center">
              <table class="table" style="min-width: 300px">
                <thead>
                  <tr>
                    <th>
                      <div>Approx.</div>
                    </th>
                    <th>
                      <div class="d-flex flex-column align-center justify-center">Amount</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div>Per Week</div>
                    </td>
                    <td>
                      <div class="text-main-color d-flex flex-column align-center justify-center">
                        {{ formatCurrency(application.fullEntitlement / 52) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>Per Month</div>
                    </td>
                    <td>
                      <div class="text-main-color d-flex flex-column align-center justify-center">
                        {{ formatCurrency(application.fullEntitlement / 12) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>Per Year</div>
                    </td>
                    <td>
                      <div class="text-main-color d-flex flex-column align-center justify-center">
                        {{ formatCurrency(application.fullEntitlement) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="content__info">
              You currently have
              <span class="text-main-color">{{ application.uk_years + application.numberOfTickBoxes }} year(s)</span> of
              National Insurance Contributions (NICs) in Total. <br />
              <br />
              <span class="text-main-color">{{ application.uk_years }}</span> originally +
              <span class="text-main-color">{{ application.numberOfTickBoxes }}</span> past years =
              <span class="text-main-color">{{ application.uk_years + application.numberOfTickBoxes }}</span>
            </div>

            <div class="request-check">
              <div class="request-check__text">
                <div>• Did HMRC Get Your Payment? We’ll Check and Tell You What They Say!</div>
                <div>• We Can Only Check if you have paid HMRC within last 12 months.</div>
              </div>
              <div class="request-check__btn">
                <button class="main-btn" @click="handleShowRequestCheckModal()">Request a Check</button>
                <div>We’ll get back to you in 5 working days.</div>
              </div>
            </div>

            <template v-if="isSeeWhatHMRCTold">
              <div class="d-flex flex-column align-center justify-center">
                <button class="green-btn w-100" @click="getRequestCheck" :disabled="isLoadingRequestCheck">
                  See what HMRC told us
                </button>
              </div>
              <div v-if="isShowRequestsCheck" class="info-container w-100">
                Following your requested check on <span class="text-main-color">{{ requestsCheck.checkDate }}</span
                >, we can confirm that HMRC received your payment of
                <span class="text-main-color">{{ formatCurrency(requestsCheck.amountPaid) }}</span> on
                <span class="text-main-color">{{ requestsCheck.paymentReceived }}</span
                >, and will allocate your payment in due course.
              </div>
            </template>
            <div class="hr"></div>
            <div class="content__title">Pension Projection</div>
            <div class="extra-years">
              <div class="content__text">
                You can buy more extra years until your retirement age (<span class="text-main-color">{{
                  application.pension_age
                }}</span
                >).<br />
                If you plan to buy into the future, <span class="text-main-color">select the amount:</span>
              </div>
              <div v-if="application.extraYearsOptions?.length" class="d-flex align-center gap-6">
                <VIcon icon="mdi-arrow-right-thick" color="primary"></VIcon>
                <select @change="handleChangeExtraYears($event)" v-model="application.extraYears">
                  <option v-for="item in application.extraYearsOptions" :value="item.value" :key="item.label">
                    {{ item.label }}
                  </option>
                </select>
              </div>
            </div>
            <div class="content__info">
              Since you’re planning to buy another
              <span class="text-main-color">{{ application.extraYears }}</span> year(s) in the future your entitlement
              will increase to approx.
              <span class="text-main-color">{{ formatCurrency(application.projection) }}</span> per year from your
              retirement age (<span class="text-main-color">{{ application.pension_age }}</span
              >).
            </div>
            <div class="d-flex flex-column align-center justify-center">
              <table class="table" style="min-width: 300px">
                <thead>
                  <tr>
                    <th>
                      <div>Approx.</div>
                    </th>
                    <th>
                      <div class="d-flex flex-column align-center justify-center">Amount</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><div>Per Week</div></td>
                    <td>
                      <div class="text-main-color d-flex flex-column align-center justify-center">
                        {{ formatCurrency(application.projection / 52) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td><div>Per Month</div></td>
                    <td>
                      <div class="text-main-color d-flex flex-column align-center justify-center">
                        {{ formatCurrency(application.projection / 12) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td><div>Per Year</div></td>
                    <td>
                      <div class="text-main-color d-flex flex-column align-center justify-center">
                        {{ formatCurrency(application.projection) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="content__info">
              Considering this, you’ll have
              <span class="text-main-color">{{
                application.uk_years + application.numberOfTickBoxes + application.extraYears
              }}</span>
              year(s) of National Insurance Contributions (NICs) in Total.
            </div>
            <div class="content__warning">
              FUTURE years currently cost
              <span class="text-main-color">{{ formatCurrency(application.c3Cost) }}</span> each.<br />
              You can <b>only</b> buy a year after the year ends on 5th April. You can <b>not</b> bulk buy future years.
            </div>
          </div>
        </PDFPrintComponent>
      </VCol>
    </VRow>
  </VContainer>
  <RequestCheckModal v-model="isShowRequestCheckModal" :modalRequestCheckYears="modalRequestCheckYears" />
</template>
<script>
import { mapActions } from 'vuex';
// Components
import RequestCheckModal from '@/components/modals/RequestCheckModal/RequestCheckModal.vue';
import PDFPrintComponent from '@/components/common/PDFPrintComponent/PDFPrintComponent.vue';
// Utils
import { formatCheckYourEntitlementData, formatRequestsCheck } from '@/utils/formatData';
// Service
import applicationService from '@/services/application-service/application.service';
export default {
  name: 'CheckYourEntitlementPage',
  components: {
    RequestCheckModal,
    PDFPrintComponent
  },
  data() {
    return {
      isLoading: true,
      isSeeWhatHMRCTold: false,
      application: {
        entitlement: 0,
        firstName: '',
        lastName: '',
        numberOfTickBoxes: 0,
        fullEntitlement: 0,
        pension_age: '-',
        uk_years: 0,
        taxYearAllocation: [],
        projection: 0,
        extraYears: 0,
        yearsToBuyLimit: 0,
        c3Cost: 0,
        extraYearsOptions: []
      },
      isLoadingSaveTaxYearAllocation: false,
      isLoadingRequestCheck: false,
      requestsCheck: {
        paymentReceived: '',
        checkDate: '',
        amountPaid: 0
      },
      isShowRequestCheckModal: false,
      modalRequestCheckYears: []
    };
  },
  mounted() {
    this.initCheckYourEntitlement();
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    async initCheckYourEntitlement() {
      this.isLoading = true;
      try {
        const { application, isSeeWhatHMRCTold } = await applicationService.initCheckYourEntitlement();
        // console.log(application);
        this.application = formatCheckYourEntitlementData(application);
        this.isSeeWhatHMRCTold = isSeeWhatHMRCTold ? true : false;
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoading = false;
      }
    },
    formatCurrency(number = 0) {
      const formatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0
      });

      const formattedNumber = formatter.format(number);
      return formattedNumber;
    },
    handleTickAll() {
      this.application.taxYearAllocation.forEach((item) => {
        item.isChecked = true;
      });
    },
    handleClearAll() {
      this.application.taxYearAllocation.forEach((item) => {
        item.isChecked = false;
      });
    },
    async handleSaveTaxYearAllocation() {
      this.isLoadingSaveTaxYearAllocation = true;
      try {
        const { application } = await applicationService.saveTaxYearAllocation({
          taxYearAllocation: this.application.taxYearAllocation
        });
        // console.log(application);
        this.application = formatCheckYourEntitlementData(application);
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoadingSaveTaxYearAllocation = false;
      }
    },
    handleChangeExtraYears() {
      this.$nextTick(() => {
        this.editExtraYears();
      });
    },
    async editExtraYears() {
      try {
        const { application } = await applicationService.editExtraYears({
          extraYears: this.application.extraYears
        });
        this.application = formatCheckYourEntitlementData(application);
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async getRequestCheck() {
      this.isLoadingRequestCheck = true;
      this.isShowRequestsCheck = false;
      try {
        const { checkRequest } = await applicationService.getRequestCheck();
        // console.log(checkRequest);
        this.requestsCheck = formatRequestsCheck(checkRequest);
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoadingRequestCheck = false;
        this.isShowRequestsCheck = true;
      }
    },
    handleShowRequestCheckModal() {
      this.isShowRequestCheckModal = true;
      this.modalRequestCheckYears = this.application.taxYearAllocation ?? [];
    },
    navigateTo(pageName) {
      if (!pageName) {
        return;
      }
      this.$router.push({ name: pageName });
    }
  }
};
</script>
